import { useTranslation } from 'react-i18next'
import AccountCard from './account-card'
import { useFormatNumber } from '../../../../../core/hooks/useFormatNumber'
import { useContext, useEffect, useState } from 'react'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'

export const sumSalesChannelPercents = (percents: TypeOfEconomyPercents) =>
  Math.round(((percents.circular ?? 0) + (percents.nonCircular ?? 0)) * 10) / 10

export interface TypeOfEconomyPercents {
  circular: number
  nonCircular: number
}

interface CardTypeOfEconomyProps {
  initialPercents: TypeOfEconomyPercents
}

const CardTypeOfEconomy = ({ initialPercents }: CardTypeOfEconomyProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const {
    forecastInstoreGmv,
    setForecastInstoreCircular,
    setForecastInstoreNonCircular,
    setTypeOfEconomyPercentTotal
  } = useContext(StoreTrajectoryContext)
  const isDisabled = forecastInstoreGmv == null || forecastInstoreGmv === 0

  const [percents, setPercents] = useState(initialPercents)

  const percentageTotal = sumSalesChannelPercents(percents)
  const isTotalMisallocated = !isDisabled && percentageTotal !== 100

  useEffect(() => {
    setTypeOfEconomyPercentTotal(percentageTotal)
  }, [percentageTotal, setTypeOfEconomyPercentTotal])

  useEffect(() => {
    if (isDisabled) {
      return
    }
    setForecastInstoreCircular(amountFromPercent(percents.circular))
    setForecastInstoreNonCircular(amountFromPercent(percents.nonCircular))
  }, [forecastInstoreGmv, isDisabled])

  const amountFromPercent = (percent: number | null) =>
    !isDisabled && percent != null ? (percent * forecastInstoreGmv) / 100 : 0

  const updatePercentAndValue =
    <K extends keyof TypeOfEconomyPercents>(propsKey: K, amountSetter: (value: number) => void) =>
    (newPercent: number) => {
      setPercents(prev => ({ ...prev, [propsKey]: newPercent }))
      amountSetter(amountFromPercent(newPercent))
    }

  return (
    <div className="mg-instore-gmv-segment-card" data-testid="instore-type-of-economy">
      <div className="mg-instore-gmv-segment-card-header">
        <div className="mg-instore-gmv-segment-card-header-title vp-subtitle-m">
          {t('general.trajectory.store.edit.instore_gmv_segment.type_of_economy.title')}
        </div>
        <div
          className={`mg-instore-gmv-segment-card-header-total vp-body-l ${isTotalMisallocated ? 'mg-instore-gmv-segment-card-header-total-error' : ''}`}
          data-testid="card-type-of-economy-total"
        >
          {formatNumber(percentageTotal / 100, '%', {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
            signDisplay: 'never'
          })}
        </div>
      </div>

      <AccountCard
        title={t('general.trajectory.store.recap.economy.circular')}
        amount={amountFromPercent(percents.circular)}
        percent={percents.circular ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('circular', setForecastInstoreCircular)}
      />

      <AccountCard
        title={t('general.trajectory.store.recap.economy.non-circular')}
        amount={amountFromPercent(percents.nonCircular)}
        percent={percents.nonCircular ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('nonCircular', setForecastInstoreNonCircular)}
      />
    </div>
  )
}

export default CardTypeOfEconomy
